import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Calendar, FileText, Award, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";

export const AssignmentsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const data = await apiCall("GET", "/assignments", null, token);
        setAssignments(data);
      } catch (err) { console.error("Failed to fetch assignments:", err); }
      setLoading(false);
    };
    if (token) fetchAssignments();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="py-8 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen" data-testid="assignments-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#002147]">Assignments</h1>
          <p className="text-[#002147]/70">View and submit your assignments</p>
        </div>
        <div className="space-y-4">
          {assignments.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="p-8 text-center">
                <FileText className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-500">No assignments found</p>
              </CardContent>
            </Card>
          ) : (
            assignments.map((assignment) => {
              const isOverdue = new Date(assignment.due_date) < new Date();
              const daysLeft = Math.ceil((new Date(assignment.due_date) - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <Card key={assignment.id} className="bg-white hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/assignment/${assignment.id}`)}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{assignment.subject}</Badge>
                          <Badge className={isOverdue ? "bg-red-100 text-red-600" : daysLeft <= 3 ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"}>
                            {isOverdue ? "Overdue" : daysLeft <= 0 ? "Due Today" : `${daysLeft} days left`}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-[#002147] mb-1">{assignment.title}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2">{assignment.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1"><User size={14} /> {assignment.teacher_name}</span>
                          <span className="flex items-center gap-1"><Calendar size={14} /> Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Award size={14} /> {assignment.max_points} pts</span>
                        </div>
                      </div>
                      <Button className="bg-[#FF6B00] hover:bg-[#E65C00] shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/assignment/${assignment.id}`); }}>
                        Submit <ArrowRight size={16} className="ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
